import React, { useEffect, useMemo, useRef, useState } from 'react';
import { geoNaturalEarth1, geoPath } from 'd3-geo';
import { feature } from 'topojson-client';
import { zoomIdentity } from 'd3-zoom';
import Supercluster from 'supercluster';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type ColumnFiltersState,
  type SortingState,
} from '@tanstack/react-table';
import { analyticsService } from '../services/api';
import { AnalyticsSessionRow, AnalyticsSummary } from '../types';
import worldMapData from '../assets/world-110m.json';

const DAY_OPTIONS = [7, 30, 90];

const formatNumber = (value: number) => value.toLocaleString();

const formatPercent = (value: number) => `${value.toFixed(1)}%`;

const formatLocalDateTime = (value?: string | null) => {
  if (!value) return '—';
  const hasTimezone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(value);
  const normalized = hasTimezone ? value : `${value}Z`;
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  });
};

const Analytics: React.FC = () => {
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sessions, setSessions] = useState<AnalyticsSessionRow[]>([]);
  const [sessionPage, setSessionPage] = useState(1);
  const [sessionPages, setSessionPages] = useState(1);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState<SortingState>([]);
  const [mapSessions, setMapSessions] = useState<AnalyticsSessionRow[]>([]);
  const [mapSessionsLoading, setMapSessionsLoading] = useState(true);
  const [mapLayerMode, setMapLayerMode] = useState<'dots' | 'heat'>('dots');
  const popupTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [selectedPoint, setSelectedPoint] = useState<{
    lat: number;
    lon: number;
    count: number;
    label: string;
    x: number;
    y: number;
  } | null>(null);
  const mapSvgRef = useRef<SVGSVGElement | null>(null);
  const mapLayerRef = useRef<SVGGElement | null>(null);

  useEffect(() => {
    let isActive = true;
    const fetchSummary = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await analyticsService.getSummary(days);
        if (isActive) {
          setSummary(data);
        }
      } catch (err) {
        if (isActive) {
          setError('Failed to load analytics summary.');
          setSummary(null);
        }
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };
    fetchSummary();
    return () => {
      isActive = false;
    };
  }, [days]);

  useEffect(() => {
    if (!selectedPoint) return;
    if (popupTimerRef.current) {
      clearTimeout(popupTimerRef.current);
    }
    popupTimerRef.current = setTimeout(() => {
      setSelectedPoint(null);
    }, 4000);
    return () => {
      if (popupTimerRef.current) {
        clearTimeout(popupTimerRef.current);
      }
    };
  }, [selectedPoint]);

  useEffect(() => {
    setSessionPage(1);
  }, [days]);

  useEffect(() => {
    let isActive = true;
    const fetchSessions = async () => {
      setSessionsLoading(true);
      try {
        const data = await analyticsService.getSessions(days, sessionPage, 50);
        if (isActive) {
          setSessions(data.sessions);
          setSessionPages(data.pagination.pages);
        }
      } catch (err) {
        if (isActive) {
          setSessions([]);
          setSessionPages(1);
        }
      } finally {
        if (isActive) {
          setSessionsLoading(false);
        }
      }
    };
    fetchSessions();
    return () => {
      isActive = false;
    };
  }, [days, sessionPage]);

  useEffect(() => {
    let isActive = true;
    const fetchMapSessions = async () => {
      setMapSessionsLoading(true);
      try {
        const perPage = 200;
        const firstPage = await analyticsService.getSessions(days, 1, perPage);
        let allSessions = [...firstPage.sessions];
        const totalPages = firstPage.pagination.pages;
        for (let page = 2; page <= totalPages; page += 1) {
          const pageData = await analyticsService.getSessions(days, page, perPage);
          allSessions = allSessions.concat(pageData.sessions);
        }
        if (isActive) {
          setMapSessions(allSessions);
        }
      } catch (err) {
        if (isActive) {
          setMapSessions([]);
        }
      } finally {
        if (isActive) {
          setMapSessionsLoading(false);
        }
      }
    };
    fetchMapSessions();
    return () => {
      isActive = false;
    };
  }, [days]);

  const conversionRate = useMemo(() => {
    if (!summary || summary.sessions === 0) return 0;
    return (summary.application_submitted / summary.sessions) * 100;
  }, [summary]);

  const returningRate = useMemo(() => {
    if (!summary || summary.sessions === 0) return 0;
    return (summary.returning_sessions / summary.sessions) * 100;
  }, [summary]);

  const mapWidth = 960;
  const mapHeight = 420;
  const worldFeatures = useMemo(() => {
    const topo = worldMapData as any;
    const raw = feature(topo, topo.objects.countries) as any;
    const filtered = raw.features.filter((item: any) => {
      const name = item?.properties?.name?.toLowerCase() || '';
      return name && !name.includes('antarctica') && !name.includes('antarctic');
    });
    return { ...raw, features: filtered };
  }, []);
  const projection = useMemo(() => {
    const proj = geoNaturalEarth1().fitSize([mapWidth, mapHeight], worldFeatures);
    proj.scale(proj.scale() * 1.12);
    const [x, y] = proj.translate();
    proj.translate([x - 12, y + 4]);
    return proj;
  }, [mapHeight, mapWidth, worldFeatures]);
  const pathGenerator = useMemo(() => geoPath(projection), [projection]);

  const pointFeatures = useMemo(() => (
    mapSessions
      .filter((session) => session.latitude != null && session.longitude != null)
      .map((session, index) => ({
        type: 'Feature' as const,
        id: index,
        properties: {
          label: [session.city, session.region].filter(Boolean).join(', ') || session.city || 'Unknown',
        },
        geometry: {
          type: 'Point' as const,
          coordinates: [Number(session.longitude), Number(session.latitude)],
        },
      }))
      .filter((feature) => (
        Number.isFinite(feature.geometry.coordinates[0]) && Number.isFinite(feature.geometry.coordinates[1])
      ))
  ), [mapSessions]);

  const sessionColumns = useMemo<ColumnDef<AnalyticsSessionRow>[]>(() => ([
    {
      header: 'Last Activity',
      accessorKey: 'last_event_at',
      cell: (info) => formatLocalDateTime(info.getValue() as string),
      sortingFn: (rowA, rowB, columnId) => {
        const a = rowA.getValue(columnId) as string | null;
        const b = rowB.getValue(columnId) as string | null;
        const aTime = a ? new Date(a).getTime() : 0;
        const bTime = b ? new Date(b).getTime() : 0;
        return aTime - bTime;
      },
    },
    {
      header: 'User',
      id: 'user',
      accessorFn: (row) => row.prospect_name || row.user_name || row.user_email || '—',
      cell: (info) => info.getValue() as string,
    },
    {
      header: 'Landing',
      accessorKey: 'landing_path',
      cell: (info) => (info.getValue() as string) || '—',
    },
    {
      header: 'Source',
      id: 'source',
      accessorFn: (row) => row.utm_source || row.referrer || 'Direct',
      cell: (info) => info.getValue() as string,
    },
    {
      header: 'Location',
      id: 'location',
      accessorFn: (row) => [row.city, row.region, row.country].filter(Boolean).join(', ') || '—',
      cell: (info) => info.getValue() as string,
    },
    {
      header: 'Browser',
      accessorKey: 'browser',
      cell: (info) => (info.getValue() as string) || '—',
    },
    {
      header: 'OS',
      accessorKey: 'os',
      cell: (info) => (info.getValue() as string) || '—',
    },
    {
      header: 'Resolution',
      id: 'resolution',
      accessorFn: (row) => (
        row.screen_width && row.screen_height ? `${row.screen_width}×${row.screen_height}` : '—'
      ),
      cell: (info) => info.getValue() as string,
    },
    {
      header: 'IP',
      accessorKey: 'ip_address',
      cell: (info) => (info.getValue() as string) || '—',
    },
  ]), []);

  const sessionTable = useReactTable({
    data: sessions,
    columns: sessionColumns,
    state: {
      sorting,
      columnFilters,
      globalFilter,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableSorting: true,
    globalFilterFn: 'includesString',
  });

  const clusterer = useMemo(() => {
    const instance = new Supercluster({
      radius: 18,
      maxZoom: 8,
      extent: 1024,
    });
    instance.load(pointFeatures as any);
    return instance;
  }, [pointFeatures]);

  const pointsById = useMemo(() => {
    const map = new Map<number, { label: string; lon: number; lat: number }>();
    pointFeatures.forEach((feature: any) => {
      if (typeof feature.id === 'number') {
        map.set(feature.id, {
          label: feature.properties?.label || 'Unknown',
          lon: feature.geometry.coordinates[0],
          lat: feature.geometry.coordinates[1],
        });
      }
    });
    return map;
  }, [pointFeatures]);

  const zoomLevel = 0;

  const clusters = useMemo(() => (
    clusterer.getClusters([-180, -85, 180, 85], zoomLevel)
  ), [clusterer, zoomLevel]);

  const maxPointCount = useMemo(() => (
    clusters.reduce((max: number, point: any) => {
      const count = point.properties?.point_count || 1;
      return Math.max(max, count);
    }, 1)
  ), [clusters]);

  const buildLocationList = (labels: Array<string | undefined>, limit = 6) => {
    const counts = new Map<string, number>();
    labels.forEach((label) => {
      if (!label) return;
      counts.set(label, (counts.get(label) || 0) + 1);
    });
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, limit)
      .map(([label, count]) => ({ label, count }));
  };

  useEffect(() => {
    if (!mapLayerRef.current) return;
    mapLayerRef.current.setAttribute('transform', zoomIdentity.toString());
  }, []);

  const trendPath = useMemo(() => {
    if (!summary || summary.time_series.length === 0) return '';
    const width = 640;
    const height = 180;
    const padding = 24;
    const maxValue = Math.max(...summary.time_series.map((entry) => entry.sessions), 1);
    return summary.time_series.map((entry, index) => {
      const x = padding + (index / Math.max(summary.time_series.length - 1, 1)) * (width - padding * 2);
      const y = padding + (1 - entry.sessions / maxValue) * (height - padding * 2);
      return `${index === 0 ? 'M' : 'L'}${x} ${y}`;
    }).join(' ');
  }, [summary]);

  return (
    <div className="space-y-6">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-1">Analytics</h1>
            <p className="text-sm text-gray-600 dark:text-slate-300">Funnel health and conversion insights</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {DAY_OPTIONS.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setDays(option)}
                className={`inline-flex items-center justify-center gap-2 font-semibold rounded-full transition-colors px-3 py-1.5 text-sm ${
                  days === option ? 'bg-emerald-600 text-white border border-emerald-600 hover:bg-emerald-700 hover:border-emerald-700' : 'bg-white/80 dark:bg-slate-900/60 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800'
                }`}
              >
                Last {option} days
              </button>
            ))}
          </div>
        </div>

      {error && (
        <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
          <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden p-5">
            <p className="text-xs uppercase tracking-wide text-gray-500">Sessions</p>
            <p className="mt-2 text-3xl font-semibold text-gray-900">{formatNumber(summary?.sessions ?? 0)}</p>
            <p className="text-xs text-gray-500 mt-1">Total visits</p>
          </div>
          <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden p-5">
            <p className="text-xs uppercase tracking-wide text-gray-500">Leads</p>
            <p className="mt-2 text-3xl font-semibold text-gray-900">{formatNumber(summary?.leads ?? 0)}</p>
            <p className="text-xs text-gray-500 mt-1">Signup or request info</p>
          </div>
          <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden p-5">
            <p className="text-xs uppercase tracking-wide text-gray-500">Apply Clicks</p>
            <p className="mt-2 text-3xl font-semibold text-gray-900">{formatNumber(summary?.apply_clicks ?? 0)}</p>
            <p className="text-xs text-gray-500 mt-1">Intent signals</p>
          </div>
          <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden p-5">
            <p className="text-xs uppercase tracking-wide text-gray-500">Applications</p>
            <p className="mt-2 text-3xl font-semibold text-gray-900">{formatNumber(summary?.application_submitted ?? 0)}</p>
            <p className="text-xs text-gray-500 mt-1">Conversion rate {formatPercent(conversionRate)}</p>
          </div>
        </div>
      <div className="mb-6 text-sm text-gray-600">
          Returning sessions: <span className="font-semibold text-gray-900">{formatNumber(summary?.returning_sessions ?? 0)}</span>{' '}
          ({formatPercent(returningRate)})
        </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden p-5">
            <p className="text-xs uppercase tracking-wide text-gray-500">Unique Visitors</p>
            <p className="mt-2 text-3xl font-semibold text-gray-900">{formatNumber(summary?.unique_visitors ?? 0)}</p>
            <p className="text-xs text-gray-500 mt-1">Distinct visitors</p>
          </div>
          <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden p-5">
            <p className="text-xs uppercase tracking-wide text-gray-500">Page Views</p>
            <p className="mt-2 text-3xl font-semibold text-gray-900">{formatNumber(summary?.page_views ?? 0)}</p>
            <p className="text-xs text-gray-500 mt-1">Total page views</p>
          </div>
        </div>

      <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden mb-6">
          <div className="px-6 py-4 border-b border-slate-100/80 dark:border-slate-700/80 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-medium text-slate-900 dark:text-slate-50">Visitor map</h2>
              <p className="text-sm text-gray-500">Recent session locations</p>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center rounded-full border border-gray-200 dark:border-transparent bg-white dark:bg-transparent p-1 text-xs">
                <button
                  type="button"
                  onClick={() => setMapLayerMode('dots')}
                  className={`inline-flex items-center justify-center gap-2 font-semibold rounded-full transition-colors px-3 py-1 text-xs ${
                    mapLayerMode === 'dots' ? 'bg-emerald-600 text-white border border-emerald-600 hover:bg-emerald-700 hover:border-emerald-700' : 'bg-white/80 dark:bg-slate-900/60 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800'
                  }`}
                >
                  Dots
                </button>
                <button
                  type="button"
                  onClick={() => setMapLayerMode('heat')}
                  className={`inline-flex items-center justify-center gap-2 font-semibold rounded-full transition-colors px-3 py-1 text-xs ${
                    mapLayerMode === 'heat' ? 'bg-emerald-600 text-white border border-emerald-600 hover:bg-emerald-700 hover:border-emerald-700' : 'bg-white/80 dark:bg-slate-900/60 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800'
                  }`}
                >
                  Heat
                </button>
              </div>
              <span className="text-xs text-gray-400">
                {mapSessionsLoading ? 'Loading sessions…' : `Last ${mapSessions.length} sessions`}
              </span>
            </div>
          </div>
          <div className="p-6">
            <div className="relative rounded-2xl border border-gray-100 bg-emerald-50 overflow-hidden">
            <svg
              viewBox={`0 0 ${mapWidth} ${mapHeight}`}
              className="w-full"
              style={{ height: '380px', display: 'block' }}
              preserveAspectRatio="xMidYMid meet"
              aria-label="Visitor map"
              ref={mapSvgRef}
            >
              <defs>
                <filter id="heatBlur">
                  <feGaussianBlur stdDeviation="6" />
                </filter>
              </defs>
              <rect width={mapWidth} height={mapHeight} fill="#ecfdf5" />
              <g ref={mapLayerRef}>
                <g fill="#e5e7eb" stroke="#c2ccd9" strokeWidth="0.5">
                  {worldFeatures.features.map((featureItem: any, index: number) => (
                    <path
                      key={`country-${featureItem.id ?? 'x'}-${index}`}
                      d={pathGenerator(featureItem) || ''}
                    />
                  ))}
                </g>
                {mapLayerMode === 'heat' ? (
                  <g filter="url(#heatBlur)" pointerEvents="none">
                    {pointFeatures.map((feature: any) => {
                      const [lon, lat] = feature.geometry.coordinates;
                      const projected = projection([lon, lat]);
                      const x = projected ? projected[0] : 0;
                      const y = projected ? projected[1] : 0;
                      return (
                        <circle
                          key={`heat-${feature.id}`}
                          cx={x}
                          cy={y}
                          r={10}
                          fill="rgba(16, 185, 129, 0.2)"
                        />
                      );
                    })}
                  </g>
                ) : (
                  clusters.map((cluster: any, index: number) => {
                    const [lon, lat] = cluster.geometry.coordinates;
                    const projected = projection([lon, lat]);
                    const x = projected ? projected[0] : 0;
                    const y = projected ? projected[1] : 0;
                    const isCluster = Boolean(cluster.properties?.cluster);
                    const count = cluster.properties?.point_count || 1;
                    const radius = isCluster ? 2 + (count / maxPointCount) * 1.2 : 1.2;
                    const label = cluster.properties?.label || 'Unknown';
                    return (
                      <circle
                        key={`${cluster.id ?? `${lon}-${lat}`}-${index}`}
                        cx={x}
                        cy={y}
                        r={radius}
                        fill={isCluster ? 'rgba(16, 185, 129, 0.5)' : 'rgba(16, 185, 129, 0.8)'}
                        stroke="rgba(5, 150, 105, 0.9)"
                        strokeWidth="0.8"
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          if (isCluster) {
                            const leafPoints = clusterer.getLeaves(cluster.id, 40);
                            const labels = leafPoints.map((leaf: any) => {
                              const info = pointsById.get(leaf.id);
                              return info?.label;
                            });
                            const locations = buildLocationList(labels, 3);
                            setSelectedPoint({
                              lat,
                              lon,
                              count,
                              label: locations[0]?.label || 'Multiple locations',
                              x,
                              y,
                            });
                            return;
                          }
                          setSelectedPoint({ lat, lon, count: 1, label, x, y });
                        }}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            if (isCluster) {
                              const leafPoints = clusterer.getLeaves(cluster.id, 40);
                              const labels = leafPoints.map((leaf: any) => {
                                const info = pointsById.get(leaf.id);
                                return info?.label;
                              });
                              const locations = buildLocationList(labels, 3);
                              setSelectedPoint({
                                lat,
                                lon,
                                count,
                                label: locations[0]?.label || 'Multiple locations',
                                x,
                                y,
                              });
                              return;
                            }
                            setSelectedPoint({ lat, lon, count: 1, label, x, y });
                          }
                        }}
                        tabIndex={0}
                      >
                        <title>
                          {isCluster
                            ? `${count} visits (zoom in)`
                            : `${label} · ${count} visit${count > 1 ? 's' : ''}`}
                        </title>
                      </circle>
                    );
                  })
                )}
              </g>
            </svg>
            {selectedPoint && (
              <div
                className="absolute z-10 rounded-lg border border-emerald-100 bg-white px-3 py-2 text-xs text-emerald-900 shadow-lg"
                style={{
                  left: `${Math.min(95, Math.max(5, (selectedPoint.x / mapWidth) * 100))}%`,
                  top: (() => {
                    const topPercent = (selectedPoint.y / mapHeight) * 100;
                    return `${Math.min(92, Math.max(8, topPercent))}%`;
                  })(),
                  transform: selectedPoint.y < mapHeight * 0.22
                    ? 'translate(-50%, 20%)'
                    : 'translate(-50%, -110%)',
                  minWidth: '160px',
                }}
              >
                <div className="font-semibold text-emerald-900">{selectedPoint.label}</div>
                <div>{selectedPoint.count} visit{selectedPoint.count > 1 ? 's' : ''}</div>
                <div className="text-[11px] text-emerald-700">
                  {selectedPoint.lat.toFixed(2)}, {selectedPoint.lon.toFixed(2)}
                </div>
              </div>
            )}
          </div>
            {!mapSessionsLoading && pointFeatures.length === 0 && (
              <p className="text-sm text-gray-500 mt-3">No location data yet.</p>
            )}
          </div>
        </div>

      <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden mb-6">
          <div className="px-6 py-4 border-b border-slate-100/80 dark:border-slate-700/80 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-medium text-slate-900 dark:text-slate-50">Session activity</h2>
              <p className="text-sm text-gray-500">Recent sessions with location and source</p>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <button
                type="button"
                onClick={() => setSessionPage((prev) => Math.max(prev - 1, 1))}
                disabled={sessionPage <= 1}
                className="px-2 py-1 rounded border border-gray-100 disabled:opacity-50"
              >
                Prev
              </button>
              <span>{sessionPage} / {sessionPages}</span>
              <button
                type="button"
                onClick={() => setSessionPage((prev) => Math.min(prev + 1, sessionPages))}
                disabled={sessionPage >= sessionPages}
                className="px-2 py-1 rounded border border-gray-100 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
          <div className="p-6">
            {sessionsLoading ? (
              <div className="text-gray-500">Loading sessions…</div>
            ) : sessions.length === 0 ? (
              <div className="text-gray-500">No sessions recorded yet.</div>
            ) : (
              <div className="space-y-3">
                <div className="flex flex-wrap items-center gap-3">
                  <input
                    type="text"
                    value={globalFilter}
                    onChange={(event) => setGlobalFilter(event.target.value)}
                    placeholder="Search all columns"
                    className="w-full max-w-xs rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700"
                  />
                  {columnFilters.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setColumnFilters([])}
                      className="text-xs text-gray-500 hover:text-gray-700"
                    >
                      Clear filters
                    </button>
                  )}
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-xs">
                    <thead className="text-[11px] uppercase text-gray-500">
                      {sessionTable.getHeaderGroups().map((headerGroup) => (
                        <tr key={headerGroup.id} className="border-b border-gray-100 dark:border-slate-700/60">
                          {headerGroup.headers.map((header) => (
                            <th key={header.id} className="px-3 py-2 text-left">
                              <button
                                type="button"
                                onClick={header.column.getToggleSortingHandler()}
                                disabled={!header.column.getCanSort()}
                                className={`flex items-center gap-1 text-left ${
                                  header.column.getCanSort() ? 'cursor-pointer select-none' : 'cursor-default'
                                }`}
                              >
                                {flexRender(header.column.columnDef.header, header.getContext())}
                                {header.column.getIsSorted() === 'asc' && '▲'}
                                {header.column.getIsSorted() === 'desc' && '▼'}
                              </button>
                            </th>
                          ))}
                        </tr>
                      ))}
                      <tr className="border-b border-gray-100 dark:border-slate-700/60">
                        {sessionTable.getLeafHeaders().map((header) => (
                          <th key={`${header.id}-filter`} className="px-3 py-2">
                            <input
                              type="text"
                              value={(header.column.getFilterValue() as string) ?? ''}
                              onChange={(event) => header.column.setFilterValue(event.target.value)}
                              placeholder="Filter"
                              className="w-full rounded border border-gray-200 px-2 py-1 text-xs text-gray-600"
                            />
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-slate-700/60 text-[11px]">
                      {sessionTable.getRowModel().rows.map((row) => (
                        <tr key={row.id}>
                          {row.getVisibleCells().map((cell) => (
                            <td key={cell.id} className="px-3 py-2 whitespace-nowrap">
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <div className="lg:col-span-2 bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100/80 dark:border-slate-700/80 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-medium text-slate-900 dark:text-slate-50">Traffic trend</h2>
                <p className="text-sm text-gray-500">Sessions by day</p>
              </div>
              <div className="text-xs text-gray-400">Last {summary?.window_days ?? days} days</div>
            </div>
            <div className="p-6">
              <div className="rounded-2xl border border-gray-100 bg-slate-50 p-4">
                {loading ? (
                  <div className="text-gray-500">Loading trend…</div>
                ) : summary?.time_series.length ? (
                  <svg viewBox="0 0 640 180" className="w-full h-[180px]">
                    <defs>
                      <linearGradient id="trendGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#34d399" stopOpacity="0.6" />
                        <stop offset="100%" stopColor="#34d399" stopOpacity="0.05" />
                      </linearGradient>
                    </defs>
                    <path d={trendPath} fill="none" stroke="#10b981" strokeWidth="3" />
                    <path d={`${trendPath} L 616 156 L 24 156 Z`} fill="url(#trendGradient)" opacity="0.4" />
                  </svg>
                ) : (
                  <div className="text-gray-500">No traffic data yet.</div>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100/80 dark:border-slate-700/80 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-medium text-slate-900 dark:text-slate-50">Funnel</h2>
                <p className="text-sm text-gray-500">From visit to application</p>
              </div>
            </div>
            <div className="p-6">
              {loading ? (
                <div className="text-gray-400">Loading funnel…</div>
              ) : (
                <ul className="space-y-3">
                  {(summary?.funnel ?? []).map((step, index) => (
                    <li key={`${step.step}-${index}`} className="flex items-center justify-between rounded-lg border border-gray-100 px-3 py-2">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{step.step}</p>
                        <p className="text-xs text-gray-500">
                          {index === 0 ? 'Baseline' : `${step.rate.toFixed(1)}% from prior`}
                        </p>
                      </div>
                      <div className="text-lg font-semibold text-gray-900">{formatNumber(step.count)}</div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100/80 dark:border-slate-700/80 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-medium text-slate-900 dark:text-slate-50">Top sources</h2>
                <p className="text-sm text-gray-500">Where leads originate</p>
              </div>
            </div>
            <div className="p-6">
              {loading ? (
                <div className="text-gray-500">Loading sources…</div>
              ) : (
                <ul className="space-y-3">
                  {(summary?.top_sources ?? []).map((source, index) => (
                    <li key={`${source.source}-${index}`} className="flex items-center justify-between text-sm">
                      <span className="text-gray-700">{source.source}</span>
                      <span className="font-semibold text-gray-900">{formatNumber(source.count)}</span>
                    </li>
                  ))}
                  {(summary?.top_sources ?? []).length === 0 && (
                    <li className="text-sm text-gray-500">No source data yet.</li>
                  )}
                </ul>
              )}
            </div>
          </div>

          <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100/80 dark:border-slate-700/80 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-medium text-slate-900 dark:text-slate-50">Top pages</h2>
                <p className="text-sm text-gray-500">Most viewed paths</p>
              </div>
            </div>
            <div className="p-6">
              {loading ? (
                <div className="text-gray-500">Loading pages…</div>
              ) : (
                <ul className="space-y-3">
                  {(summary?.top_pages ?? []).map((page, index) => (
                    <li key={`${page.page_path}-${index}`} className="flex items-center justify-between text-sm">
                      <span className="text-gray-700">{page.page_path || 'Unknown'}</span>
                      <span className="font-semibold text-gray-900">{formatNumber(page.count)}</span>
                    </li>
                  ))}
                  {(summary?.top_pages ?? []).length === 0 && (
                    <li className="text-sm text-gray-500">No page data yet.</li>
                  )}
                </ul>
              )}
            </div>
          </div>

          <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100/80 dark:border-slate-700/80 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-medium text-slate-900 dark:text-slate-50">Application steps</h2>
                <p className="text-sm text-gray-500">Step completion volume</p>
              </div>
            </div>
            <div className="p-6">
              {loading ? (
                <div className="text-gray-500">Loading steps…</div>
              ) : (
                <ul className="space-y-3">
                  {(summary?.step_completion ?? []).map((step, index) => (
                    <li key={`${step.step}-${index}`} className="flex items-center justify-between text-sm">
                      <span className="text-gray-700">{step.step}</span>
                      <span className="font-semibold text-gray-900">{formatNumber(step.count)}</span>
                    </li>
                  ))}
                  {(summary?.step_completion ?? []).length === 0 && (
                    <li className="text-sm text-gray-500">No step data yet.</li>
                  )}
                </ul>
              )}
            </div>
          </div>
        </div>

      <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100/80 dark:border-slate-700/80 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-medium text-slate-900 dark:text-slate-50">Top programs</h2>
              <p className="text-sm text-gray-500">Interest and conversion signals</p>
            </div>
          </div>
          <div className="p-6">
            {loading ? (
              <div className="text-gray-500">Loading programs…</div>
            ) : summary?.top_programs.length ? (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="text-xs uppercase text-gray-500">
                    <tr className="border-b border-gray-100">
                      <th className="px-3 py-2 text-left">Program ID</th>
                      <th className="px-3 py-2 text-left">Views</th>
                      <th className="px-3 py-2 text-left">Apply Clicks</th>
                      <th className="px-3 py-2 text-left">Submissions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {summary.top_programs.map((program) => (
                      <tr key={program.program_id}>
                        <td className="px-3 py-2">{program.program_id}</td>
                        <td className="px-3 py-2">{formatNumber(program.views)}</td>
                        <td className="px-3 py-2">{formatNumber(program.apply_clicks)}</td>
                        <td className="px-3 py-2">{formatNumber(program.submissions)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-gray-500">No program data yet.</div>
            )}
          </div>
        </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden p-5">
            <p className="text-xs uppercase tracking-wide text-gray-500">Applications Started</p>
            <p className="mt-2 text-3xl font-semibold text-gray-900">{formatNumber(summary?.application_started ?? 0)}</p>
            <p className="text-xs text-gray-500 mt-1">Opened application flow</p>
          </div>
          <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden p-5">
            <p className="text-xs uppercase tracking-wide text-gray-500">Info Session Registrations</p>
            <p className="mt-2 text-3xl font-semibold text-gray-900">{formatNumber(summary?.info_session_registrations ?? 0)}</p>
            <p className="text-xs text-gray-500 mt-1">Registrations submitted</p>
          </div>
      </div>
    </div>
  );
};

export default Analytics;
